import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

export function LoginPage() {
    const { user, signIn, signInWithGoogle, loading: authLoading } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Redirect if already logged in
    if (user && !authLoading) {
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        const { error } = await signIn(email, password);

        if (error) {
            setError(
                error.message === 'Invalid login credentials'
                    ? 'Nieprawidłowy email lub hasło'
                    : error.message
            );
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            {/* Dot Pattern Background */}
            {/* Dot Pattern Background */}
            <div className="bg-grid-pattern" />

            <div className="w-full max-w-[400px] relative z-10">
                {/* Logo */}
                <div className="text-center mb-8">
                    <h1 className="text-[24px] tracking-[0.15em] font-medium mb-2">WYSKO</h1>
                    <p className="text-sm text-muted-foreground">
                        Zaloguj się do swojego konta
                    </p>
                </div>

                {/* Card */}
                <div className="bg-card rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-border/50 p-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Error Alert */}
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="break-words">{error}</span>
                            </div>
                        )}

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[13px]">
                                Adres e-mail
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="twoj@email.pl"
                                    className="pl-10 h-10"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-[13px]">
                                    Hasło
                                </Label>
                                <Link
                                    to="/reset-password"
                                    className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Zapomniałeś hasła?
                                </Link>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="pl-10 pr-10 h-10"
                                    required
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit */}
                        <Button
                            type="submit"
                            className="w-full h-10"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Logowanie...</span>
                                </div>
                            ) : (
                                'Zaloguj się'
                            )}
                        </Button>
                    </form>

                    {/* OAuth Buttons */}
                    <div className="space-y-3 mt-4 mb-6">
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full h-10 font-normal relative"
                            onClick={signInWithGoogle}
                        >
                            <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 absolute left-4" alt="Google" />
                            Kontynuuj z Google
                        </Button>
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[12px] text-muted-foreground">lub</span>
                        <div className="flex-1 h-px bg-border" />
                    </div>

                    {/* Register Link */}
                    <p className="text-center text-[13px] text-muted-foreground">
                        Nie masz jeszcze konta?{' '}
                        <Link
                            to="/register"
                            className="text-foreground font-medium hover:underline"
                        >
                            Zarejestruj się
                        </Link>
                    </p>
                </div>

                {/* Footer */}
                <p className="text-center text-[11px] text-muted-foreground mt-6">
                    Logując się, akceptujesz{' '}
                    <a href="#" className="underline hover:text-foreground">
                        Regulamin
                    </a>{' '}
                    i{' '}
                    <a href="#" className="underline hover:text-foreground">
                        Politykę Prywatności
                    </a>
                </p>
            </div>
        </div>
    );
}
